import React, { useCallback, useEffect, useRef, useState } from 'react';
import { chapterAt, formatTimecode, VIDEO_MODE } from './videos';

function PhoneFrame({ children, size = 'hero', glow = 'orange', enter = false }) {
  return (
    <div className="phone-stage" data-size={size} data-glow={glow} data-enter={enter ? 'true' : 'false'}>
      <div className="phone-glow" aria-hidden="true" />
      <div className="phone-shadow" aria-hidden="true" />
      <div className="phone-chassis">
        <span className="phone-btn phone-btn-silent" aria-hidden="true" />
        <span className="phone-btn phone-btn-vol-up" aria-hidden="true" />
        <span className="phone-btn phone-btn-vol-down" aria-hidden="true" />
        <span className="phone-btn phone-btn-power" aria-hidden="true" />
        <div className="phone-bezel">
          <div className="phone-island" aria-hidden="true" />
          {children}
          <div className="phone-glass" aria-hidden="true" />
          <div className="phone-home-bar" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

export default function PhoneMockup({
  video,
  size = 'hero',
  glow = 'orange',
  lazy = true,
  enter = false,
  controlsTone = 'dark',
  onChapterChange,
  onEnded,
}) {
  const videoRef = useRef(null);
  const rootRef = useRef(null);
  const [inView, setInView] = useState(!lazy);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [posterSrc, setPosterSrc] = useState(video.poster);
  const [activeSrc, setActiveSrc] = useState(video.mp4);
  const [chapter, setChapter] = useState(video.chapters[0]);

  useEffect(() => {
    if (!lazy) {
      setInView(true);
      return undefined;
    }
    const node = rootRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '480px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    setPosterSrc(video.poster);
    setChapter(video.chapters[0]);
    setActiveSrc(video.mp4);
    setFailed(false);
    setReady(false);
    setProgress(0);
    setCurrentTime(0);
    onChapterChange?.(video.chapters[0]);
  }, [video.id, video.mp4, video.poster]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      await el.play();
      setIsPlaying(true);
      setNeedsGesture(false);
    } catch {
      setIsPlaying(false);
      setNeedsGesture(true);
    }
  }, []);

  const pause = () => {
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  const replay = () => {
    setFailed(false);
    setReady(false);
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    play();
  };

  return (
    <div className="loksy-phone-wrap" ref={rootRef}>
      <PhoneFrame size={size} glow={glow} enter={enter}>
        <div className="phone-screen">
          <img
            src={posterSrc}
            alt=""
            onError={() => {
              if (posterSrc !== video.posterFallback) setPosterSrc(video.posterFallback);
            }}
          />
          {inView && !failed && activeSrc ? (
            <video
              ref={videoRef}
              key={activeSrc}
              className="phone-fade"
              style={{ opacity: ready ? 1 : 0 }}
              src={activeSrc}
              autoPlay={VIDEO_MODE === 'normal'}
              muted={isMuted}
              loop={!onEnded}
              playsInline
              preload="auto"
              poster={posterSrc}
              aria-label={video.ariaLabel}
              onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
              onLoadedData={() => {
                setReady(true);
                setFailed(false);
              }}
              onCanPlay={() => {
                setReady(true);
                setFailed(false);
                if (isPlaying) play();
              }}
              onPlaying={() => {
                setReady(true);
                setFailed(false);
                setIsPlaying(true);
              }}
              onTimeUpdate={() => {
                const el = videoRef.current;
                if (!el?.duration) return;
                setCurrentTime(el.currentTime);
                setProgress(el.currentTime / el.duration);
                const next = chapterAt(video.chapters, el.currentTime);
                if (next.time !== chapter.time) {
                  setChapter(next);
                  onChapterChange?.(next);
                }
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => onEnded?.()}
              onError={() => {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[Loksy] video unavailable', video.id, activeSrc);
                }
                setFailed(true);
                setReady(false);
              }}
            />
          ) : null}

          {failed ? (
            <div className="phone-fallback">
              <p className="loksy-display" style={{ margin: 0, fontSize: 18 }}>
                {video.title}
              </p>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>{video.tagline}</p>
              <button type="button" className="loksy-btn loksy-btn-ghost" onClick={replay}>
                Replay
              </button>
            </div>
          ) : null}

          {needsGesture && !failed ? (
            <button type="button" className="phone-play-gate" onClick={play} aria-label="Watch demo">
              <span className="loksy-display">Watch Demo</span>
            </button>
          ) : null}
        </div>
      </PhoneFrame>

      <div className={`phone-controls ${controlsTone === 'light' ? 'light' : ''}`}>
        <button type="button" onClick={() => (isPlaying ? pause() : play())} aria-label={isPlaying ? 'Pause video' : 'Play video'}>
          {isPlaying ? '❚❚' : '▶'}
        </button>
        <label className="phone-progress">
          <span className="loksy-sr">Video progress</span>
          <input
            type="range"
            min={0}
            max={1000}
            value={Math.round(progress * 1000)}
            onChange={(event) => {
              const el = videoRef.current;
              if (!el?.duration) return;
              el.currentTime = (Number(event.target.value) / 1000) * el.duration;
            }}
          />
        </label>
        <span style={{ fontSize: 11, minWidth: 72 }} className="loksy-sr">
          {formatTimecode(currentTime)} / {formatTimecode(duration)}
        </span>
        <button type="button" onClick={() => setIsMuted((value) => !value)} aria-label={isMuted ? 'Unmute video' : 'Mute video'}>
          {isMuted ? '🔇' : '🔊'}
        </button>
        <button type="button" onClick={replay} aria-label="Replay video">
          ↺
        </button>
      </div>
    </div>
  );
}
